import {RequireSome} from '@wandb/weave/common/types/base';
import {
  LayoutType,
  MaskOptions,
  MediaCardMetadata,
  MediaCardType,
} from '@wandb/weave/common/types/media';
import {GroupedList} from '@wandb/weave/common/util/data';
import {get, map, sum} from 'lodash';

import {metadataHasCaptions} from '../../util/media';
import {PanelProps} from '../../util/panelProps';
import {MediaBrowserPanelConfig} from './common';
import {Dimensions, getGroupedMediaDimensions} from './dimensions';
import type {GridData, SlotRegistry} from './layout/types';
import {fitToAvailableDimensions} from './sizing/fitToAvailableDimensions';
import {Tile} from './tiles/types';

export type MediaBrowserPanelProps = PanelProps<MediaBrowserPanelConfig>;
export interface RangeSettings {
  min: number;
  max: number;
  current: number;
  active?: boolean;
  label?: string;
}

export type SizingSettings = {
  columnCount: {
    min: number;
    max: number;
    current: number;
  };
};

export type Layout = {
  tiles: GroupedList<Tile>;
  galleryAxisData?: number[] | null;
  gridAxisData?: {
    x: number[];
    y: number[];
  } | null;
  // Maintain full scope x axis data so we can preserve zoom
  xAxisTicks?: number[];
  allXAxisValues?: number[];
  historySampleRate?: number;
  slots?: SlotRegistry;
  gridData?: GridData;
};

export type MediaConfigWithDefaultsAndMediaKeys = RequireSome<
  MediaConfigWithDefaults,
  'mediaKeys'
>;

export const maskOptionsForLayout = (
  layoutType: LayoutType | null,
  maskKeys: string[]
): MaskOptions[] => {
  if (layoutType === null) {
    layoutType = 'MASKS_NEXT_TO_IMAGE';
  }
  if (layoutType === 'ALL_STACKED') {
    return [{maskKeys, showImage: true}];
  } else if (layoutType === 'ALL_SPLIT') {
    return [
      {
        maskKeys: [],
        showImage: true,
      },
      ...map(maskKeys, k => ({
        maskKeys: [k],
        showImage: false,
      })),
    ];
  } else if (layoutType === 'MASKS_NEXT_TO_IMAGE') {
    return [
      {
        maskKeys: [],
        showImage: true,
      },
      {
        maskKeys,
        showImage: false,
      },
    ];
  } else {
    // This case should never be hit it is used to hint the type system
    throw new Error('INVALID LAYOUT TYPE');
  }
};

export function calcGroupCount(config: MediaBrowserPanelConfig): number {
  return sum(
    map(config.mediaKeys, m => {
      const tileCount = get(config.tileLayout, [m, 'maskOptions', 'length'], 1);
      return tileCount;
    })
  );
}

type PanelContentSizing = {
  cardWidth: number;
  cardHeight: number;
  shrunk: boolean;
};

type GetPanelContentSizingParams = {
  config: Omit<MediaBrowserPanelConfig, 'chartTitle'>;
  mediaBrowserSize: {
    width: number;
    height: number;
  };
  sizingSettings: SizingSettings;
  mediaType: MediaCardType;
  firstMediaMetadata: MediaCardMetadata | null;
};

const TOP_AND_BOTTOM_PADDING = 25;
const SPACE_BETWEEN_COLUMNS = 6;
const CAPTION_LINE_HEIGHT = 22;

export function getPanelContentSizing({
  config: {actualSize, fitToDimension},
  mediaType,
  mediaBrowserSize,
  sizingSettings,
  firstMediaMetadata,
}: GetPanelContentSizingParams): PanelContentSizing {
  const hasCaptions = metadataHasCaptions(firstMediaMetadata);
  const availableWidth =
    mediaBrowserSize.width -
    (sizingSettings.columnCount.current + 1) * SPACE_BETWEEN_COLUMNS;
  const availableHeight =
    mediaBrowserSize.height -
    TOP_AND_BOTTOM_PADDING -
    (hasCaptions ? CAPTION_LINE_HEIGHT : 0);

  const groupedDimensions = getGroupedMediaDimensions(firstMediaMetadata);
  const mediaWidth = groupedDimensions?.width;
  const mediaHeight = groupedDimensions?.height;

  const defaultCardWidth = availableWidth / sizingSettings.columnCount.current;
  const defaultCardHeight = availableHeight;

  const {width: cardWidth, height: cardHeight} = getCardDimensions();

  // If the tiles don't fit in the media panel we want to shrink them
  // and change the layout so they're centered
  const heightOverflows = cardHeight > availableHeight;
  if (!actualSize && !fitToDimension && heightOverflows) {
    const aspectRatio = (mediaWidth ?? cardWidth) / (mediaHeight ?? cardHeight);
    return {
      cardWidth: availableHeight * aspectRatio,
      cardHeight: availableHeight,
      shrunk: true,
    };
  }

  return {
    cardWidth,
    cardHeight,
    shrunk: false,
  };

  function getCardDimensions(): Dimensions {
    if (mediaType === 'audio') {
      return {width: defaultCardWidth, height: 100};
    }
    if (mediaType === 'html' || mediaType === 'plotly') {
      return {width: defaultCardWidth, height: mediaBrowserSize.height};
    }
    if (mediaType === 'image' || mediaType === 'video') {
      return getImageDimensions();
    }
    return {width: defaultCardWidth, height: defaultCardWidth};
  }

  function getImageDimensions(): Dimensions {
    const width = mediaWidth ?? defaultCardWidth;
    const height = mediaHeight ?? defaultCardHeight;
    if (actualSize) {
      return {
        width,
        height,
      };
    }
    if (fitToDimension) {
      return fitToAvailableDimensions(
        [availableHeight, availableWidth],
        [height, width]
      );
    }
    // Size all cards by the column width and adjust the height
    // To maintain the aspect ratio
    return {
      width: defaultCardWidth,
      height: (defaultCardWidth / width) * height,
    };
  }
}

// A type for the config that contains defaults.
// Allows for cleaner type logic
export type MediaConfigWithDefaults = RequireSome<
  MediaBrowserPanelConfig,
  | 'actualSize'
  | 'fitToDimension'
  | 'columnCount'
  | 'mode'
  | 'pixelated'
  | 'gallerySettings'
  | 'gridSettings'
  | 'maxYAxisCount'
  | 'snapToExistingStep'
>;
